import React, { useState, useEffect } from 'react';
import { Modal, ModalBody, Row, Col, Button } from 'reactstrap';
import { Icon } from '../../../../components/Component';
import PhoneInput, { isValidPhoneNumber, isPossiblePhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { toast } from 'react-toastify';
import { activeStatus, newRoles } from '../../../../utils/envConfig'; // Assuming newRoles might be needed if role editing is complex

const LeadEditModal = ({ isOpen, leadData, axiosInstance, toggle, onSubmitSuccess }) => {
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({}); // For local form errors if needed, or rely on toast
    const [errorX, setErrorX] = useState(null); // For phone input error display

    useEffect(() => {
        if (leadData) {
            setFormData({
                ...leadData,
                // Ensure active status is correctly mapped if it's not already
                active: leadData.active === activeStatus[0] || leadData.active === true ? activeStatus[0] : activeStatus[1],
            });
        }
    }, [leadData]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handlePhoneChange = (value) => {
        setFormData((prev) => ({ ...prev, phone: value }));
        if (value && !isPossiblePhoneNumber(value, 'AE')) {
            setErrorX('Phone number is not possible');
        } else if (value && !isValidPhoneNumber(value, 'AE')) {
            setErrorX('Phone number is not valid');
        } else {
            setErrorX(null);
        }
    };

    const validateEdit = (currentFormData) => {
        let isError = false;
        let newErrors = {};

        if (!currentFormData.first_name || currentFormData.first_name.length < 2) {
            toast.error('Enter a valid First Name');
            newErrors.firstName = 'Enter a valid First Name';
            isError = true;
        }

        if (!currentFormData.phone || !currentFormData.phone.trim()) {
            toast.error('Enter valid phone number');
            newErrors.phone = 'Enter valid phone number';
            isError = true;
        } else {
            const isPossible = isPossiblePhoneNumber(currentFormData.phone, 'AE');
            if (!isPossible) {
                toast.error('Phone number is not possible');
                newErrors.phone = 'Phone number is not possible';
                isError = true;
            } else {
                const isValidNum = isValidPhoneNumber(currentFormData.phone, 'AE');
                if (!isValidNum) {
                    toast.error('Phone number is not valid');
                    newErrors.phone = 'Phone number is not valid';
                    isError = true;
                }
            }
        }

        if (!currentFormData.role) {
            toast.error('Select the Role');
            newErrors.role = 'Select the Role'; // Assuming role might be part of formData if editable here
            isError = true;
        }

        setErrors(newErrors);
        if (isError) {
            // toast.error('Invalid fields. Please check your inputs.'); // General message if needed
        }
        return !isError;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateEdit(formData)) {
            return;
        }

        try {
            const newApiFormData = {
                ...formData,
                first_name: formData.first_name, // Ensure correct mapping if state uses different keys
                last_name: formData.last_name,
                email: formData.email,
                phone: formData?.phone?.trim()?.replaceAll(' ', ''),
                active: formData.active === activeStatus[0], // Convert "Active"/"Inactive" back to boolean
                // leadSourceId: formData?.leadSourceId?.id ?? '', // If lead source is editable here
            };

            // Remove userId from payload if it's part of formData from parent but not needed in PATCH body
            const { id, ...payload } = newApiFormData;


            await axiosInstance.patch(`/users/${leadData.id}`, payload); // Use leadData.id for the endpoint

            // Handle agent role assignment if applicable (this logic might be complex and better handled in parent or service layer)
            if ([newRoles.Agent, newRoles.AnonymousAgent, newRoles.TeamLeader].includes(formData.role)) {
                try {
                    // This part might need adjustment based on how agent creation/linking is handled
                    // For simplicity, assuming this logic might be refactored or handled by parent
                    // const agentUserResponse = await axiosInstance.get(`/users/useragent/${leadData.id}`);
                    // const agentPayload = { userId: agentUserResponse.data.id };
                    // await axiosInstance.post(`/agent`, agentPayload);
                    console.log("Agent role specific logic would be here if needed.");
                } catch (err) {
                    // toast.error('User not assigned Agent Role / Inactive User !');
                    console.error("Error in agent role assignment part:", err);
                }
            }

            toast.success('User modified with changes!');
            if (onSubmitSuccess) {
                onSubmitSuccess(); // This will trigger refetch in parent
            }
            toggle(); // Close modal
        } catch (err) {
            if (err.response && err.response.data && err.response.data.message && err.response.data.message.includes('duplicate key')) {
                toast.error('User not added. Email already exists!');
            } else {
                toast.error(err?.response?.data?.message || 'User not modified with changes!');
            }
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} className="modal-dialog-centered" size="lg">
            <ModalBody>
                <a
                    href="#cancel"
                    className="close"
                    onClick={(ev) => {
                        ev.preventDefault();
                        toggle();
                    }}
                >
                    <Icon name="cross-sm" />
                </a>
                <div className="p-2">
                    <h5 className="title">Edit User</h5>
                    <div className="mt-4">
                        <form onSubmit={handleSubmit}>
                            <Row className="g-3">
                                <Col md="6">
                                    <div className="form-group">
                                        <label className="form-label" htmlFor="edit-firstName">
                                            First Name
                                        </label>
                                        <div className="form-control-wrap">
                                            <input
                                                type="text"
                                                className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                                                id="edit-firstName"
                                                name="first_name" // Ensure name matches formData key
                                                value={formData?.first_name || ''}
                                                onChange={handleInputChange}
                                            />
                                            {errors.firstName && <span className="invalid">{errors.firstName}</span>}
                                        </div>
                                    </div>
                                </Col>
                                <Col md="6">
                                    <div className="form-group">
                                        <label className="form-label" htmlFor="edit-lastName">
                                            Last Name
                                        </label>
                                        <div className="form-control-wrap">
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="edit-lastName"
                                                name="last_name" // Ensure name matches formData key
                                                value={formData?.last_name || ''}
                                                onChange={handleInputChange}
                                            />
                                            {/* Add error display if needed */}
                                        </div>
                                    </div>
                                </Col>
                                <Col md="6">
                                    <div className="form-group">
                                        <label className="form-label" htmlFor="edit-email">
                                            Email
                                        </label>
                                        <div className="form-control-wrap">
                                            <input
                                                type="email"
                                                className="form-control"
                                                id="edit-email"
                                                name="email"
                                                value={formData?.email || ''}
                                                onChange={handleInputChange}
                                            />
                                            {/* Add error display if needed */}
                                        </div>
                                    </div>
                                </Col>
                                <Col md="6">
                                    <div className="form-group mb-2">
                                        <label className="form-label" htmlFor="edit-phone">
                                            Phone
                                        </label>
                                        <PhoneInput
                                            disabled={formData.DndStatus} // Assuming DndStatus comes from leadData
                                            className={`phoneInput ${errors.phone ? 'is-invalid' : ''}`}
                                            id="edit-phone"
                                            defaultCountry="AE"
                                            value={(!formData.DndStatus && formData?.phone) || ''}
                                            name="phone"
                                            placeholder={
                                                formData.DndStatus ? "Can't edit DND numbers" : 'Enter Your Phone *'
                                            }
                                            onChange={handlePhoneChange}
                                        />
                                        {errors.phone && <div className="text-danger small mt-1"> {errors.phone}</div>}
                                        {errorX && !errors.phone && <div className="text-danger small mt-1"> {errorX}</div>}
                                    </div>
                                </Col>
                                {/* Add other fields like Role, Status if they are editable in this modal */}
                                {/* Example for Status (active) - if it's editable here */}
                                {/* <Col md="6">
                  <div className="form-group">
                    <label className="form-label" htmlFor="edit-active">Status</label>
                    <RSelect
                      options={activeStatus.map(s => ({value: s, label: s}))}
                      value={{value: formData.active, label: formData.active}}
                      onChange={(selected) => setFormData({...formData, active: selected.value})}
                    />
                  </div>
                </Col> */}
                                <Col size="12" className="text-end">
                                    <Button color="primary" type="submit" className="mt-3">
                                        <Icon className="plus"></Icon>
                                        <span>Save Changes</span>
                                    </Button>
                                </Col>
                            </Row>
                        </form>
                    </div>
                </div>
            </ModalBody>
        </Modal>
    );
};

export default LeadEditModal;